import { useState, type FormEvent } from "react";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { ScreenHeader } from "../../components/ScreenHeader";

export interface SignInValues {
  firstName: string;
  lastName: string;
  email: string;
  participantId: string;
}

interface SignInScreenProps {
  remembered: SignInValues | null;
  busy: boolean;
  error: string | null;
  onSubmit: (values: SignInValues) => void;
  onForget: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Participant sign-in: program email and GATEWAY participant ID, no password. */
export function SignInScreen({ remembered, busy, error, onSubmit, onForget }: SignInScreenProps) {
  const [values, setValues] = useState<SignInValues>(
    remembered ?? { firstName: "", lastName: "", email: "", participantId: "" },
  );
  const [touched, setTouched] = useState(false);

  const problems = {
    firstName: values.firstName.trim() ? null : "Enter your first name.",
    lastName: values.lastName.trim() ? null : "Enter your last name.",
    email: EMAIL_RE.test(values.email.trim()) ? null : "Please enter a valid email address.",
    participantId:
      values.participantId.trim().length >= 4
        ? null
        : "Enter your participant ID, it's on your enrollment confirmation.",
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (Object.values(problems).some(Boolean)) return;
    onSubmit({
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim().toLowerCase(),
      participantId: values.participantId.trim(),
    });
  };

  const field = (key: keyof SignInValues, label: string, props: Record<string, string>) => (
    <Field label={label} htmlFor={key} error={touched ? problems[key] : null}>
      <input
        id={key}
        className={inputShell}
        value={values[key]}
        onChange={(event) => setValues({ ...values, [key]: event.target.value })}
        aria-invalid={touched && Boolean(problems[key])}
        aria-describedby={touched && problems[key] ? `${key}-error` : undefined}
        {...props}
      />
    </Field>
  );

  return (
    <form onSubmit={submit} noValidate className="max-w-[440px]">
      <ScreenHeader
        kicker="Welcome"
        title="Let's get you work-ready, one step at a time."
        sub="This is your Growth Pathway workspace. Each step builds something real: a live profile, a portfolio, winning bids, a payment setup. By Step 4 you're confirmed Work-Enabled. Sign in with the details your program has on record."
      />
      {remembered ? (
        <p className="mb-3.5 text-[13px] text-text-muted">
          Not {remembered.firstName || "you"}?{" "}
          <button type="button" className="text-accent underline" onClick={onForget}>
            Use different details
          </button>
        </p>
      ) : null}
      {field("firstName", "First name", { autoComplete: "given-name", placeholder: "e.g. Amara" })}
      {field("lastName", "Last name", { autoComplete: "family-name", placeholder: "e.g. Okafor" })}
      {field("email", "Your program email", {
        type: "email",
        autoComplete: "email",
        placeholder: "you@example.com",
      })}
      {field("participantId", "Your GATEWAY participant ID", {
        autoComplete: "off",
        placeholder: "e.g. GTW-04213",
      })}
      {error ? (
        <p role="alert" className="mb-3 text-[13.5px] text-negative">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={busy}>
        {busy ? "Signing in..." : "Continue →"}
      </Button>
      <div className="mt-5 rounded-[var(--radius-control)] border border-border bg-surface-high p-4 text-[13.5px]">
        <b>Two important things:</b> use the exact same email your program/LMS has on record, and
        the participant ID from your enrollment confirmation. Together they're how your progress is
        matched to you. Coming back later on this same link picks up exactly where you left off.
      </div>
    </form>
  );
}
